import { Component, EventEmitter, Output, ViewChild } from "@angular/core";
import * as XLSX from "xlsx";
import {
  FileUpload,
  FileUploadHandlerEvent,
  FileUploadModule,
} from "primeng/fileupload";
import { SalesOrderExcelTrackingDto } from "../../../../core/models/orders/salesOrderExcelTrackingDto";
import { CommonModule } from "@angular/common";
import { ButtonModule } from "primeng/button";

@Component({
  selector: "jni-order-tracking-excel",
  templateUrl: "./order-tracking-excel.component.html",
  styleUrls: ["./order-tracking-excel.component.scss"],
  imports: [FileUploadModule, CommonModule, ButtonModule],
})
export class OrderTrackingExcelComponent {
  @ViewChild("workflowFileUpload") fileUpload!: FileUpload;
  uploadedFiles: File[] = [];

  @Output() closeEvent = new EventEmitter();
  @Output() excelData = new EventEmitter<SalesOrderExcelTrackingDto[]>();
  @Output() downloadExcelTemplateEvent = new EventEmitter();

  onClose() {
    this.closeEvent.emit();
  }

  onUpload(event: FileUploadHandlerEvent) {
    const file = event.files[0];
    this.uploadedFiles = [file]; // Add file to uploadedFiles array for display
    const reader: FileReader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      /* create workbook */
      const result = e.target?.result;
      if (!result) {
        return;
      }
      const data = new Uint8Array(result as ArrayBuffer);
      const wb: XLSX.WorkBook = XLSX.read(data, { type: "array" });

      /* selected the first sheet */
      const wsname: string = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];

      /* save data */
      const rows = XLSX.utils.sheet_to_json(ws) as SalesOrderExcelTrackingDto[]; // to get 2d array pass 2nd parameter as object {header: 1}

      this.excelData.emit(rows);
    };
    reader.readAsArrayBuffer(file);
    this.clearUploadFile();
  }

  downloadTemplate() {
    this.downloadExcelTemplateEvent.emit();
  }

  isDragOver = false;

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];

      // Validate file type
      if (
        !file.name.toLowerCase().endsWith(".xlsx") &&
        !file.name.toLowerCase().endsWith(".xls")
      ) {
        // You might want to add a toastr service here for error messages
        console.error("Please select an Excel file (.xlsx or .xls)");
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        console.error("File size must be less than 10MB");
        return;
      }

      if (this.fileUpload) {
        this.fileUpload.files = [file];
        // Trigger the upload
      }
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  removeFile(): void {
    this.uploadedFiles = [];
    // Clear PrimeNG's internal file list
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
  }

  clearUploadFile() {
    this.uploadedFiles = [];
    // Clear PrimeNG's internal file list
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
  }
}
